// src/poses/norwegianTargets.js
// ----------------------------------------------------------------------------
// Feature-driven targets (canonical) + SVG render compatibility layer
//
// Goal:
// - Keep your existing feature fields (lips/velum/tip/body/jaw/timing/glide).
// - ALSO provide `render` for EVERY entry so ArticulationSideView never crashes.
//
// Renderer contract (current ArticulationSideView):
//   target.render = {
//     rest?:    { show: string[] },
//     closure?: { show: string[] },
//     release?: { show: string[], burst?: "strong"|"weak", voicing?: boolean },
//     static?:  { show: string[], burst?:..., voicing?:... } // used for non-stops
//   }
//
// SVG expectations (ids):
//   - upper_jaw_lips_open_velum_rest
//   - upper_jaw_lips_closed_velum_rest
//   - upper_jaw_lips_open_velum_raised
//   - lower_jaw_lips_open
//   - lower_jaw_lips_closed
// Anchors:
//   - anchor_mouth_exit
//   - anchor_glottis
// ----------------------------------------------------------------------------

// ---- Canonical feature definitions (yours) ---------------------------------

const RAW = {
  // stops (already have good render)
  p: {
    render: {
      rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
      closure: { show: ["upper_jaw_lips_closed_velum_rest", "lower_jaw_lips_closed"] },
      release: { show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"], burst: "strong", voicing: false },
    },
    lips: "closed",
    velum: "raised",
    tip: "neutral",
    body: "neutral",
    jaw: "neutral",
    timing: { burst: "strong" },
  },

  b: {
    render: {
      rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
      closure: { show: ["upper_jaw_lips_closed_velum_rest", "lower_jaw_lips_closed"] },
      release: { show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"], burst: "weak", voicing: true },
    },
    lips: "closed",
    velum: "raised",
    tip: "neutral",
    body: "neutral",
    jaw: "neutral",
    timing: { voicing: "on", burst: "weak" },
  },

  // stops (feature-only for now)
 // --- stops (add explicit render so closure exists for tongue stops) ---------

t: {
  render: {
    rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
    // NOTE: your SVG set currently only has *lip* closure layers.
    // This "closure" is still useful as a stage marker (timing), even if visuals
    // look similar until you add tongue-contact layers later.
    closure: { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open_tip_alveolar"] },
    release: { show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"], burst: "strong", voicing: false },
  },
  lips: "open",
  velum: "raised",
  tip: "alveolar",
  body: "neutral",
  jaw: "neutral",
  timing: { burst: "strong" },
},

d: {
  render: {
    rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
    closure: { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open_tip_alveolar"] },
    release: { show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"], burst: "weak", voicing: true },
  },
  lips: "open",
  velum: "raised",
  tip: "alveolar",
  body: "neutral",
  jaw: "neutral",
  timing: { voicing: "on", burst: "weak" },
},

  k: {
  render: {
    rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
    // No dedicated "tongue dorsum closure" SVG yet → keep visuals stable,
    // but keep the closure stage so stop timing works.
    closure: { show: ["upper_jaw_lips_open_velum_rest",  "lower_jaw_lips_open_tongue_back_high"] },
    release: { show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"], burst: "strong", voicing: false },
  },
  lips: "open",
  velum: "raised",
  tip: "neutral",
  body: "back_high",
  jaw: "neutral",
  timing: { burst: "strong" },
},

"ɡ": {
  render: {
    rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
    closure: { show: ["upper_jaw_lips_open_velum_rest",  "lower_jaw_lips_open_tongue_back_high"] },
    release: { show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"], burst: "weak", voicing: true },
  },
  lips: "open",
  velum: "raised",
  tip: "neutral",
  body: "back_high",
  jaw: "neutral",
  timing: { voicing: "on", burst: "weak" },
},
  // nasals
  m: { lips: "closed", velum: "lowered", tip: "neutral", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  n: { lips: "open", velum: "lowered", tip: "alveolar", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  "ŋ": { lips: "open", velum: "lowered", tip: "neutral", body: "back_high", jaw: "neutral", timing: { voicing: "on" } },

  // fricatives/approximants
  f: { lips: "open", velum: "raised", tip: "neutral", body: "neutral", jaw: "neutral" },
  "ʋ": { lips: "open", velum: "raised", tip: "neutral", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  s: { lips: "open", velum: "raised", tip: "alveolar", body: "neutral", jaw: "neutral" },
  "ʃ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral" },
  "ç": { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
  j: { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral", timing: { voicing: "on" } },
  h: { lips: "open", velum: "raised", tip: "neutral", body: "neutral", jaw: "neutral" },

  // liquids
  r: { lips: "open", velum: "raised", tip: "alveolar", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  l: { lips: "open", velum: "raised", tip: "alveolar", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },

  // retroflex series
  "ʈ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { burst: "strong" } },
  "ɖ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  "ɳ": { lips: "open", velum: "lowered", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  "ɭ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },

  // vowels (feature-only, for now)
  i:   { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
  "iː":{ lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral", timing: { length: "long" } },

  y:   { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
  "yː":{ lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral", timing: { length: "long" } },

  e:   { lips: "open", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral" },
  "eː":{ lips: "open", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral", timing: { length: "long" } },

  "ø":   { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral" },
  "øː":  { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral", timing: { length: "long" } },

  "æ":   { lips: "open", velum: "raised", tip: "neutral", body: "front_low", jaw: "lowered" },
  "æː":  { lips: "open", velum: "raised", tip: "neutral", body: "front_low", jaw: "lowered", timing: { length: "long" } },

  "ɑ":   { lips: "open", velum: "raised", tip: "neutral", body: "back_low", jaw: "lowered" },
  "ɑː":  { lips: "open", velum: "raised", tip: "neutral", body: "back_low", jaw: "lowered", timing: { length: "long" } },

  "ʉ":   { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral" },
  "ʉː":  { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral", timing: { length: "long" } },

  u:    { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_high", jaw: "neutral" },
  "uː": { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_high", jaw: "neutral", timing: { length: "long" } },

  o:    { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_mid", jaw: "neutral" },
  "oː": { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_mid", jaw: "neutral", timing: { length: "long" } },

  // diphthongs (feature-only metadata for now)
  ei: {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "front_mid",  jaw: "neutral" },
      end:   { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  "æi": {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "front_low",  jaw: "lowered" },
      end:   { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  "ɑi": {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "back_low",   jaw: "lowered" },
      end:   { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  "øʉ": {
    type: "diphthong",
    glide: {
      start: { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid",    jaw: "neutral" },
      end:   { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral" },
    },
  },
  "æʉ": {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "front_low", jaw: "lowered" },
      end:   { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral" },
    },
  },
  "øy": {
    type: "diphthong",
    glide: {
      start: { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid",  jaw: "neutral" },
      end:   { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  oy: {
    type: "diphthong",
    glide: {
      start: { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_mid",   jaw: "neutral" },
      end:   { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
};

// ---- Render fallback builder -----------------------------------------------

const LAYERS = {
  upper_open_rest:   "upper_jaw_lips_open_velum_rest",
  upper_closed_rest: "upper_jaw_lips_closed_velum_rest",
  upper_open_raised: "upper_jaw_lips_open_velum_raised",
  lower_open:        "lower_jaw_lips_open",
  lower_closed:      "lower_jaw_lips_closed",
};

function inferVoicing(entry) {
  const v = entry?.timing?.voicing;
  return v === "on" || v === true || v === "true";
}

function inferBurst(entry) {
  const b = entry?.timing?.burst;
  if (b === "strong" || b === "weak") return b;
  return null;
}

function inferLipClosure(entry) {
  return String(entry?.lips || "").toLowerCase() === "closed";
}

function buildFallbackRender(entry) {
  // If author provided render already, keep it.
  if (entry?.render && typeof entry.render === "object") return entry.render;

  const voicing = inferVoicing(entry);
  const burst = inferBurst(entry);
  const lipsClosed = inferLipClosure(entry);

  // Conservative guess:
  // - rest: open lips, velum rest (neutral)
  // - closure: only if lips are "closed"
  // - release: open lips, velum raised; attach burst/voicing if any
  // - static: for non-stops, just show a stable open/raised frame
  const render = {
    rest: { show: [LAYERS.upper_open_rest, LAYERS.lower_open] },
    ...(lipsClosed
      ? { closure: { show: [LAYERS.upper_closed_rest, LAYERS.lower_closed] } }
      : {}),
    release: {
      show: [LAYERS.upper_open_raised, LAYERS.lower_open],
      ...(burst ? { burst } : {}),
      ...(voicing ? { voicing: true } : {}),
    },
    // Used by future code / vowel display if you decide to use it:
    static: {
      show: [LAYERS.upper_open_raised, LAYERS.lower_open],
      ...(voicing ? { voicing: true } : {}),
    },
  };

  return render;
}

// Export: feature-first objects + guaranteed `.render`
export const NO_TARGETS = Object.fromEntries(
  Object.entries(RAW).map(([ipa, entry]) => {
    const safe = { ...(entry || {}) };
    safe.render = buildFallbackRender(safe);
    return [ipa, safe];
  })
);